/**
 * Client-side attachment constants and helpers for the agent chat.
 * Kept in lockstep with the agent backend (``src/agent/core/file_parts.py``).
 * The backend enforces the same limits — these values only keep the UI honest
 * so a user never uploads a file that the server will reject.
 */

export const AGENT_CHAT_MAX_FILES_PER_MESSAGE = 3;
export const AGENT_CHAT_MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MiB

/** Accepted IANA media types; a loose ``image/*`` allowance keeps this forward-compatible. */
export const AGENT_CHAT_ACCEPTED_MIME_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
  'text/csv',
  'application/vnd.ms-excel', // legacy .xls + some CSV browsers report this for .csv
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
];

/** Value suitable for ``<input type="file" accept>``. */
export const AGENT_CHAT_ACCEPT_ATTRIBUTE = [
  ...AGENT_CHAT_ACCEPTED_MIME_TYPES,
  '.csv',
  '.xls',
  '.xlsx',
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
].join(',');

/**
 * Build a ``FileList`` from validated ``File`` objects for ``useChat``'s
 * ``sendMessage({ text, files })`` (AI SDK converts to ``file`` UI parts).
 */
export function fileListFromFiles(files: readonly File[]): FileList {
  const dt = new DataTransfer();
  for (const f of files) {
    dt.items.add(f);
  }
  return dt.files;
}

/** Human-readable file size (e.g. ``"2.3 MB"``). */
export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type AgentAttachmentValidationError =
  | { kind: 'tooManyFiles'; max: number }
  | { kind: 'fileTooLarge'; filename: string; maxBytes: number }
  | { kind: 'unsupportedType'; filename: string; mediaType: string };

export type AgentAttachmentValidationResult = {
  accepted: File[];
  errors: AgentAttachmentValidationError[];
};

function isAcceptedType(file: File): boolean {
  const t = (file.type || '').toLowerCase();
  if (!t) {
    // Browsers sometimes drop the MIME for CSV/XLSX; fall back to extension.
    const name = file.name.toLowerCase();
    return (
      name.endsWith('.csv') ||
      name.endsWith('.xls') ||
      name.endsWith('.xlsx') ||
      name.endsWith('.pdf') ||
      name.endsWith('.png') ||
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg') ||
      name.endsWith('.webp')
    );
  }
  return AGENT_CHAT_ACCEPTED_MIME_TYPES.includes(t) || t.startsWith('image/');
}

/**
 * Validate a batch of files against current attachments.
 * ``existingCount`` is the number of files already attached; it is counted
 * toward the per-message cap so callers don't have to pre-merge the lists.
 */
export function validateAgentAttachments(
  incoming: ReadonlyArray<File>,
  existingCount: number,
  {
    maxFiles = AGENT_CHAT_MAX_FILES_PER_MESSAGE,
    maxBytes = AGENT_CHAT_MAX_FILE_BYTES,
  }: { maxFiles?: number; maxBytes?: number } = {}
): AgentAttachmentValidationResult {
  const errors: AgentAttachmentValidationError[] = [];
  const accepted: File[] = [];
  let remaining = Math.max(0, maxFiles - existingCount);
  for (const f of incoming) {
    if (!isAcceptedType(f)) {
      errors.push({ kind: 'unsupportedType', filename: f.name, mediaType: f.type || 'unknown' });
      continue;
    }
    if (f.size > maxBytes) {
      errors.push({ kind: 'fileTooLarge', filename: f.name, maxBytes });
      continue;
    }
    if (remaining <= 0) {
      errors.push({ kind: 'tooManyFiles', max: maxFiles });
      break;
    }
    accepted.push(f);
    remaining -= 1;
  }
  return { accepted, errors };
}

export function formatAgentAttachmentError(err: AgentAttachmentValidationError): string {
  switch (err.kind) {
    case 'tooManyFiles':
      return `You can attach at most ${err.max} files per message.`;
    case 'fileTooLarge':
      return `${err.filename} is too large (max ${formatAttachmentSize(err.maxBytes)}).`;
    case 'unsupportedType':
      return `${err.filename} is an unsupported type (${err.mediaType}).`;
  }
}
